import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, BaseResponseModel, LatLng, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { City } from '@/models/city.model';
import { getCity } from '@/services/city.service';
import CityForm from './city-form';
import CityDiscoveryPoints from './city-discovery-points';
import RouteList from '@/pages/route/list/route-list';
import CityDistrictList from '../city-district/city-district-list';
import CityRunEventKey from './city-run-event-key';
import CityGeometryForm from './city-geometry';
import CityCells from './city-cells';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="city-form">City Detail</Nav.Item>
      <Nav.Item eventKey="city-geometry" disabled={newMode}>City Geometry</Nav.Item>
      <Nav.Item eventKey="city-cells" disabled={newMode}>H3 Cells</Nav.Item>
      <Nav.Item eventKey="discovery-points" disabled={newMode}>Discovery Points</Nav.Item>
      <Nav.Item eventKey="city-districts" disabled={newMode}>Districts</Nav.Item>
      <Nav.Item eventKey="routes" disabled={newMode}>Routes</Nav.Item>
      <Nav.Item eventKey="run-event-keys" disabled={newMode}>Run Event Keys</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;
  const countryId = useLocation()?.state?.countryId;

  const [activeTab, setActiveTab] = useState('city-form');
  const [formObject, setFormObject] = useState<City|undefined>(undefined);
  const [cityLatLog, setCityLatLog] = useState<LatLng|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<City> = await getCity(id);
        if (result.success) {
          setFormObject(result.result);
          if(result.result.lat && result.result.lon){
            setCityLatLog({lat: result.result.lat, lng: result.result.lon});
          }
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching city: ${errorModel?.errorMessage}`);
        nav('/cities');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit City: ${formObject.name}` : 'New City'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/cities'> Cities</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'city-form' && <CityForm formObject={formObject} countryId={countryId} pageState={pageState}/>}
          {activeTab == 'city-geometry' && <CityGeometryForm city={formObject} jsonGeometry={formObject?.jsonGeometry} cityFetchData={fetchData}/>}
          {activeTab == 'city-cells' && <CityCells city={formObject}/>}
          {activeTab == 'discovery-points' && id && <CityDiscoveryPoints cityId={id} mapCenter={cityLatLog}/>}
          {activeTab == 'city-districts' && id && <CityDistrictList cityId={id} mapCenter={cityLatLog}/>}
          {activeTab == 'routes' && id && <RouteList cityId={id} />}
          {activeTab == 'run-event-keys' && id && <CityRunEventKey parentCity={formObject}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
